import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import apiClient from "../services/apiClient";

interface SessionContextType {
  sessionId: string | null;
  isSessionActive: boolean;
  createSession: (userId: string) => Promise<string>;
  endSession: (id?: string) => Promise<void>;
  checkSessionStatus: (id?: string) => Promise<boolean>;
}

const SessionContext = createContext<SessionContextType | null>(null);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isSessionActive, setIsSessionActive] = useState(false);

  const checkSessionStatus = useCallback(async (id?: string): Promise<boolean> => {
    const idToCheck = id || sessionId;
    if (!idToCheck) return false;

    try {
      const { data } = await apiClient.get(`/sessions/${idToCheck}/status`);
      const active = data.success && data.active;
      if (idToCheck === sessionId) {
        setIsSessionActive(active);
      }
      return active;
    } catch (error: any) {
      console.error("Error checking session status:", error);
      if (idToCheck === sessionId) {
        setIsSessionActive(false);
      }
      return false;
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;

    const checkInterval = setInterval(async () => {
      const active = await checkSessionStatus();
      if (!active) {
        console.warn("Session expired or inactive");
        setSessionId(null);
        setIsSessionActive(false);
      }
    }, 60000); // check every minute

    return () => clearInterval(checkInterval);
  }, [sessionId, checkSessionStatus]);

  const createSession = useCallback(async (userId: string): Promise<string> => {
    try {
      console.log(`Creating new session for user: ${userId}`);
      const { data } = await apiClient.post("/sessions/create", {
        user_id: userId,
      });

      if (data.success && data.session_id) {
        setSessionId(data.session_id);
        setIsSessionActive(true);
        console.log(`Session created: ${data.session_id}`);

        // store in sessionStorage for page refresh persistence
        sessionStorage.setItem("activeSessionId", data.session_id);
        sessionStorage.setItem("activeUserId", userId);

        return data.session_id;
      } else {
        throw new Error("Failed to create session");
      }
    } catch (error: any) {
      console.error("Error creating session:", error);
      throw error;
    }
  }, []);

  const endSession = useCallback(async (id?: string) => {
    const idToEnd = id || sessionId;
    if (!idToEnd) return;

    try {
      console.log(`Ending session: ${idToEnd}`);
      await apiClient.post(`/sessions/${idToEnd}/end`);

      if (idToEnd === sessionId) {
        setSessionId(null);
        setIsSessionActive(false);
        sessionStorage.removeItem("activeSessionId");
        sessionStorage.removeItem("activeUserId");
      }
      console.log("Session ended successfully");
    } catch (error: any) {
      console.error("Error ending session:", error);
    }
  }, [sessionId]);

  // using sendBeacon to end session on page unload because this way we are sure that the async calls will be sent
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionId && isSessionActive) {
        // Try sendBeacon first (works in most browsers)
        const url = `${apiClient.defaults.baseURL}/sessions/${sessionId}/end`;
        const blob = new Blob([JSON.stringify({})], {
          type: "application/json",
        });

        const sent = navigator.sendBeacon(url, blob);

        if (!sent) {
          // Fallback: synchronous XHR (deprecated but works for cleanup)
          console.warn("sendBeacon failed, using fallback");
          try {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", url, false); // false = synchronous
            xhr.setRequestHeader("Content-Type", "application/json");
            xhr.send(JSON.stringify({}));
          } catch (e) {
            console.error("Failed to end session on unload", e);
          }
        }

        // Clear storage regardless
        sessionStorage.removeItem("activeSessionId");
        sessionStorage.removeItem("activeUserId");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [sessionId, isSessionActive]);

  return (
    <SessionContext.Provider
      value={{
        sessionId,
        isSessionActive,
        createSession,
        endSession,
        checkSessionStatus,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};
